import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  LanguageTextListRes,
  LanguageRecord,
} from "../../types/LanguageTextList";

const STORAGE_KEY = "i18n_dict_v1";

export type I18nCachePayload = {
  language_id: number | null;
  code?: string;
  version?: number;
  map: Record<string, string>;
  mapNorm?: Record<string, string>;
  Records: LanguageRecord[];
};

/** Lowercase + strip punctuation so minor key mismatches still resolve. */
export function normalizeI18nKey(key: string): string {
  return String(key)
    .toLowerCase()
    .replace(/[,.!?;:'"«»()[\]{}]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function recordsToMap(records: LanguageRecord[] = []): Record<string, string> {
  const map: Record<string, string> = {};
  for (const r of records) {
    if (r?.key != null) {
      map[r.key] = r.Translation ?? r.key;
    }
  }
  return map;
}

export function recordsToNormMap(
  records: LanguageRecord[] = [],
): Record<string, string> {
  const mapNorm: Record<string, string> = {};
  for (const r of records) {
    if (r?.key == null) continue;
    const nk = normalizeI18nKey(r.key);
    if (!nk) continue;
    // Prefer first exact translation; don't overwrite with identical key fallbacks
    if (mapNorm[nk] == null) {
      mapNorm[nk] = r.Translation ?? r.key;
    }
  }
  return mapNorm;
}

function readCache(): I18nCachePayload | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as I18nCachePayload;
    if (!parsed?.map || typeof parsed.map !== "object") return null;
    if (!parsed.mapNorm) {
      parsed.mapNorm = recordsToNormMap(parsed.Records || []);
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeI18nCache(payload: I18nCachePayload) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // quota / private mode — ignore
  }
}

function buildStateFromCache(): LanguageTextListRes {
  const cache = readCache();
  if (!cache) {
    return {
      status: true,
      data: { Records: [], map: {}, mapNorm: {} },
      message: "",
      toast: false,
    };
  }
  return {
    status: true,
    data: {
      Records: cache.Records || [],
      map: cache.map,
      mapNorm: cache.mapNorm || recordsToNormMap(cache.Records || []),
      version: cache.version,
      code: cache.code,
      language_id: cache.language_id ?? undefined,
    },
    message: "",
    toast: false,
  };
}

const initialState: LanguageTextListRes = buildStateFromCache();

const LanguageTextListSlice = createSlice({
  name: "LanguageTextList",
  initialState,
  reducers: {
    updateLanguageTextList(state, action: PayloadAction<LanguageTextListRes>) {
      const payload = action.payload;
      const records = payload?.data?.Records || [];
      const map =
        payload?.data?.map && Object.keys(payload.data.map).length
          ? payload.data.map
          : recordsToMap(records);
      const mapNorm =
        payload?.data?.mapNorm && Object.keys(payload.data.mapNorm).length
          ? payload.data.mapNorm
          : recordsToNormMap(records);

      const next: LanguageTextListRes = {
        ...payload,
        data: {
          ...payload.data,
          Records: records,
          map,
          mapNorm,
        },
      };

      writeI18nCache({
        language_id: next.data.language_id ?? null,
        code: next.data.code,
        version: next.data.version,
        map,
        mapNorm,
        Records: records,
      });

      return next;
    },
  },
});

export default LanguageTextListSlice.reducer;
export const { updateLanguageTextList } = LanguageTextListSlice.actions;
export { readCache, STORAGE_KEY };
